import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

import {
  getBucketName,
  getBucketRegion,
  useFileUpload,
  useAppContext,
} from "@examspark/common-app";
import {
  Button,
  CautionBanner,
  ConfirmModal,
  Dropzone,
  HSpace,
  RouterLink,
  Text,
  Toolbar,
  Truncate,
  VSpace,
} from "@examspark/common-ui";
import {
  IconDocumentLine,
  IconLinkLine,
  IconPencilLine,
  IconTrashLine,
  IconUploadLine,
} from "@examspark/common-ui/dist/components/icons";
import { RichContentEditor } from "@examspark/common-ui-rce5";
import { Annotation, Resource } from "@examspark/types";

import { getLinkForResource } from "../../utils";
import UnitContent from "../common/UnitContent";
import NewLinkModal from "../resources/NewLinkModal";

const Styled = styled.div`
  border-top: solid 1px #ccc;
`;
const StyledInner = styled(VSpace)`
  padding: 1rem;
`;
const StyledLabel = styled(Text.H4)`
  font-size: 1.1rem;
  text-transform: uppercase;
`;
const AnnotationResources = styled(VSpace)`
  border-top: solid 1px #ddd;
  padding-top: 1rem;
  gap: 0.5rem;
`;
const ResourceListItem = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 1rem;
`;
const ResourceListItemIcon = styled.div`
  svg {
    fill: #555;
  }
`;
const ListItemMeta = styled.div`
  flex: 1;
  min-width: 0;
`;
const ListItemButtons = styled.div``;

const EmptyAnnotation = styled.div`
  border: dashed 1px #ddd;
  padding: 2rem;
  text-align: center;
  color: #888;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  border-radius: 1rem;
`;

type Props = {
  annotations: Annotation[] | undefined;
  label?: string;
  placeholder?: string;
  canEdit?: boolean;
  onCreate: (resource?: Resource) => Promise<void>;
  onDelete: (annotation: Annotation) => Promise<void>;
  onUpdate: (annotation: Annotation) => Promise<void>;
  onEditing?: (isEditing: boolean) => void;
};

const AnnotationDetail = ({
  annotations,
  canEdit,
  onCreate,
  onDelete,
  onUpdate,
  onEditing,
  label,
  placeholder,
}: Props) => {
  const [state] = useAppContext();
  const [isEditing, setIsEditingInternal] = useState(false);
  const [droppedFiles, updateDroppedFiles] = useFileUpload({
    workspaceId: state.workspaceUser?.id,
    bucketName: getBucketName(),
    region: getBucketRegion(),
  });
  const [content, setContent] = useState<string | null>(null);
  const uploadButtonRef = useRef<HTMLInputElement>(null);
  const [isNewLinkModalOpen, setIsNewLinkModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const annotation = useMemo(
    () => (annotations?.filter((a) => !a.resourceId) ?? [])[0],
    [annotations],
  );
  const fileAnnotations = useMemo(
    () => annotations?.filter((a) => a.resourceId) ?? [],
    [annotations],
  );

  const setIsEditing = useCallback(
    (isEditing: boolean) => {
      setIsEditingInternal(isEditing);
      onEditing?.(isEditing);
    },
    [onEditing],
  );

  useEffect(() => {
    setContent(annotation?.content?.body ?? null);
    setIsEditing(false);
  }, [annotation, setIsEditing]);

  const handleDrop = (acceptedFiles: File[]) => {
    acceptedFiles.forEach(async (file) => {
      updateDroppedFiles.addFile(file);
    });
  };

  const handleCreateClick = async () => {
    await onCreate();

    // Timeout to order this after the above useEffect
    setTimeout(() => setIsEditing(true));
  };

  const handleSaveChangesClick = async () => {
    if (annotation == null) return;

    await onUpdate({
      ...annotation,
      content: {
        body: content,
      },
    });

    setIsEditing(false);
  };

  const onCancelChangesClick = useCallback(() => {
    setIsEditing(false);
    setContent(annotation?.content?.body ?? null);
  }, [annotation, setIsEditing]);

  return (
    <>
      <NewLinkModal
        isOpen={isNewLinkModalOpen}
        onRequestClose={() => setIsNewLinkModalOpen(false)}
        onComplete={async (link) => {
          await onCreate(link);
          setIsNewLinkModalOpen(false);
        }}
      />
      <Styled>
        <Dropzone
          browseButtonRef={uploadButtonRef}
          onDrop={(accepted) => {
            handleDrop(accepted);
          }}
        >
          <StyledInner>
            <StyledLabel>{label}</StyledLabel>
            {annotation == null && (
              <div>
                <EmptyAnnotation>
                  <div>
                    {placeholder ??
                      "Save your own notes to this document for reference anytime."}
                  </div>
                  {canEdit && (
                    <div>
                      <Button
                        title="Create Annotation"
                        onClick={handleCreateClick}
                        icon={<IconPencilLine />}
                      >
                        Create Annotation
                      </Button>
                    </div>
                  )}
                </EmptyAnnotation>
              </div>
            )}
            {annotation != null && (
              <VSpace>
                {canEdit && isEditing && (
                  <>
                    <RichContentEditor
                      variant="inline"
                      value={content}
                      onChange={setContent}
                    />
                    <Toolbar>
                      <Button
                        title="Save Changes"
                        variant="contained"
                        color="primary"
                        icon={<IconPencilLine />}
                        onClick={handleSaveChangesClick}
                      >
                        Save Changes
                      </Button>
                      <Button
                        title="Cancel"
                        variant="contained"
                        color="secondary"
                        onClick={onCancelChangesClick}
                      >
                        Cancel
                      </Button>
                    </Toolbar>
                  </>
                )}
                {!isEditing &&
                  (content ? (
                    <UnitContent content={content} />
                  ) : (
                    <i>Annotation has no content. Click Edit to modify it.</i>
                  ))}
              </VSpace>
            )}
            {canEdit && !isEditing && (
              <HSpace size="0.5rem">
                {droppedFiles.length === 0 && (
                  <>
                    {annotation != null && (
                      <>
                        <Button
                          title="Edit Annotation"
                          variant="plain"
                          color="secondary"
                          icon={<IconPencilLine />}
                          onClick={() => setIsEditing(true)}
                        >
                          Edit
                        </Button>
                        <Button
                          title="Clear Annotation"
                          variant="plain"
                          icon={<IconTrashLine />}
                          onClick={async () => {
                            const result = await ConfirmModal.show({
                              children: (
                                <div style={{ padding: "1rem" }}>
                                  <CautionBanner variant="plain">
                                    Are you sure you want to clear your
                                    annotation? Files and Web Links will remain.
                                  </CautionBanner>
                                </div>
                              ),
                            });

                            if (result) {
                              onDelete(annotation);
                            }
                          }}
                        >
                          Clear
                        </Button>
                      </>
                    )}
                    <Button
                      title="Add Web Link"
                      variant="plain"
                      icon={<IconLinkLine />}
                      onClick={() => setIsNewLinkModalOpen(true)}
                    >
                      Web Link
                    </Button>
                    <Button
                      title="Upload"
                      variant="plain"
                      icon={<IconUploadLine />}
                      onClick={() => uploadButtonRef.current?.click()}
                    >
                      Upload
                    </Button>
                  </>
                )}
                {droppedFiles.length > 0 && (
                  <>
                    <Button
                      title="Upload Files"
                      variant="contained"
                      color="primary"
                      isLoading={isUploading}
                      disabled={isUploading}
                      icon={<IconUploadLine />}
                      onClick={async () => {
                        try {
                          setIsUploading(true);
                          const uploadedFiles =
                            await updateDroppedFiles.uploadFiles();
                          if (!uploadedFiles) return;
                          for (const uploadedFile of uploadedFiles) {
                            await onCreate(uploadedFile);
                          }
                        } finally {
                          updateDroppedFiles.clearFiles();
                          setIsUploading(false);
                        }
                      }}
                    >
                      Upload Files
                    </Button>
                    <Button
                      title="Upload Files"
                      variant="plain"
                      onClick={() => updateDroppedFiles.clearFiles()}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </HSpace>
            )}
            {!!fileAnnotations.length && (
              <div>
                <AnnotationResources>
                  {fileAnnotations.map((annotation, i) => {
                    return (
                      <ResourceListItem key={i}>
                        <ResourceListItemIcon>
                          {annotation.resource?.type === "EXTERNAL_LINK" ? (
                            <IconLinkLine width="16" />
                          ) : (
                            <IconDocumentLine width="16" />
                          )}
                        </ResourceListItemIcon>
                        <ListItemMeta>
                          <Truncate style={{ padding: "4px" }}>
                            {annotation.resource ? (
                              <RouterLink
                                to={getLinkForResource(annotation.resource)}
                              >
                                {annotation.resource?.name}
                              </RouterLink>
                            ) : (
                              "Invalid Resource"
                            )}
                          </Truncate>
                        </ListItemMeta>
                        <ListItemButtons>
                          <HSpace size="0.5rem">
                            <Button
                              variant="plain"
                              title="Remove Resource"
                              icon={<IconTrashLine width="16" />}
                              onClick={async () => {
                                const result = await ConfirmModal.show({
                                  children: (
                                    <div style={{ padding: "1rem" }}>
                                      <CautionBanner variant="plain">
                                        Are you sure you want to remove this
                                        file?
                                      </CautionBanner>
                                    </div>
                                  ),
                                });
                                if (!result) return;
                                await onDelete(annotation);
                              }}
                            />
                          </HSpace>
                        </ListItemButtons>
                      </ResourceListItem>
                    );
                  })}
                </AnnotationResources>
              </div>
            )}
            {!!droppedFiles.length && (
              <div>
                <AnnotationResources>
                  {droppedFiles.map((file, i) => {
                    return (
                      <ResourceListItem key={i}>
                        <ListItemMeta>
                          <Truncate>{file.file.name}</Truncate>
                        </ListItemMeta>
                        <div>{file.status}</div>
                        <ListItemButtons>
                          <HSpace size="0.5rem">
                            <Button
                              variant="plain"
                              title="Remove Resource"
                              icon={<IconTrashLine width="16" />}
                              onClick={async () => {
                                updateDroppedFiles.deleteAtIndex(i);
                              }}
                            />
                          </HSpace>
                        </ListItemButtons>
                      </ResourceListItem>
                    );
                  })}
                </AnnotationResources>
              </div>
            )}
          </StyledInner>
        </Dropzone>
      </Styled>
    </>
  );
};

export default AnnotationDetail;
